import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../lib/utils'
import type { StyleType } from '../types/podcast-generation'
import { BookOpen, MessageCircle, Sparkles, Search, ChevronDown } from 'lucide-react'

interface StylePickerProps {
    value: StyleType
    onChange: (value: StyleType) => void
    disabled?: boolean
}

interface StyleOption {
    value: StyleType
    label: string
    shortLabel: string
    description: string
    icon: React.ElementType
    graphic: React.ReactNode
}

const STYLE_OPTIONS: StyleOption[] = [
    {
        value: 'conversational',
        label: 'Conversational',
        shortLabel: 'Chat',
        description: 'Casual & friendly',
        icon: MessageCircle,
        graphic: (
            <div className="relative w-full h-full flex flex-col items-center justify-center p-1.5 gap-1">
                {/* Chat bubbles with host labels */}
                <div className="flex items-end gap-1 w-full">
                    <div className="w-4 h-4 rounded-full bg-slate-300 shrink-0 flex items-center justify-center">
                        <span className="text-[6px] font-bold text-white">A</span>
                    </div>
                    <div className="flex-1 bg-slate-100 rounded-lg rounded-bl-none px-1.5 py-1 border border-slate-200">
                        <span className="text-[7px] text-slate-500 leading-none">Hey, what do you think...</span>
                    </div>
                </div>
                <div className="flex items-end gap-1 w-full flex-row-reverse">
                    <div className="w-4 h-4 rounded-full bg-brand-400 shrink-0 flex items-center justify-center">
                        <span className="text-[6px] font-bold text-white">B</span>
                    </div>
                    <div className="flex-1 bg-brand-50 rounded-lg rounded-br-none px-1.5 py-1 border border-brand-200">
                        <span className="text-[7px] text-brand-600 leading-none">Oh totally! I love that...</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        value: 'educational',
        label: 'Educational',
        shortLabel: 'Learn',
        description: 'Clear & structured',
        icon: BookOpen,
        graphic: (
            <div className="relative w-full h-full flex flex-col items-start justify-center p-1.5 gap-0.5">
                {/* Step-by-step learning with numbered steps */}
                <div className="flex items-center gap-1.5 w-full">
                    <div className="w-3.5 h-3.5 rounded-full bg-brand-500 shrink-0 flex items-center justify-center">
                        <span className="text-[7px] font-bold text-white">1</span>
                    </div>
                    <div className="flex-1 h-1.5 bg-brand-200 rounded-full" />
                </div>
                <div className="flex items-center gap-1.5 w-full">
                    <div className="w-3.5 h-3.5 rounded-full bg-brand-400 shrink-0 flex items-center justify-center">
                        <span className="text-[7px] font-bold text-white">2</span>
                    </div>
                    <div className="flex-1 h-1.5 bg-brand-100 rounded-full" />
                </div>
                <div className="flex items-center gap-1.5 w-full">
                    <div className="w-3.5 h-3.5 rounded-full bg-brand-300 shrink-0 flex items-center justify-center">
                        <span className="text-[7px] font-bold text-white">3</span>
                    </div>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full" />
                </div>
            </div>
        ),
    },
    {
        value: 'narrative',
        label: 'Storytelling',
        shortLabel: 'Story',
        description: 'Engaging & dramatic',
        icon: Sparkles,
        graphic: (
            <div className="relative w-full h-full flex flex-col items-center justify-center p-1">
                {/* Story arc with chapter labels */}
                <div className="w-full flex justify-between px-0.5 mb-0.5">
                    <span className="text-[5px] font-medium text-slate-400">INTRO</span>
                    <span className="text-[5px] font-medium text-brand-500">CLIMAX</span>
                    <span className="text-[5px] font-medium text-slate-400">END</span>
                </div>
                <svg className="w-full h-[60%] text-brand-400" viewBox="0 0 48 20" fill="none" preserveAspectRatio="none">
                    <path 
                        d="M2 16 C 10 16 14 4 24 4 C 34 4 38 14 46 14" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round"
                        fill="none"
                    />
                    <circle cx="24" cy="4" r="2.5" fill="currentColor" className="text-brand-500" />
                </svg>
                <span className="text-[6px] text-slate-400 mt-0.5">Story Arc</span>
            </div>
        ),
    },
    {
        value: 'deep dive',
        label: 'Deep Dive',
        shortLabel: 'Deep',
        description: 'Thorough & analytical',
        icon: Search,
        graphic: (
            <div className="relative w-full h-full flex flex-col items-center justify-center p-1">
                {/* Layered depth visualization */}
                <div className="relative w-full flex-1 flex flex-col gap-0.5">
                    <div className="flex items-center gap-1 opacity-50">
                        <div className="w-2 h-2 rounded-sm bg-slate-200" />
                        <span className="text-[6px] text-slate-400">Surface</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-70 pl-1">
                        <div className="w-2 h-2 rounded-sm bg-slate-300" />
                        <span className="text-[6px] text-slate-500">Details</span>
                    </div>
                    <div className="flex items-center gap-1 pl-2">
                        <div className="w-2 h-2 rounded-sm bg-brand-400" />
                        <span className="text-[6px] text-brand-600 font-medium">Insights</span>
                    </div>
                </div>
                <div className="w-full h-px bg-gradient-to-r from-transparent via-brand-300 to-transparent mt-0.5" />
            </div>
        ),
    },
]

export function StylePicker({ value, onChange, disabled }: StylePickerProps) {
    const [isOpen, setIsOpen] = useState(false)
    const selectedOption = STYLE_OPTIONS.find(o => o.value === value) || STYLE_OPTIONS[0]
    const SelectedIcon = selectedOption.icon

    return (
        <div className="w-full">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                    "flex items-center justify-between w-full p-2 -ml-2 rounded-lg transition-colors group",
                    "hover:bg-gray-50 text-left outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/20"
                )}
            >
                <div>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">Presentation Style</span>
                    <div className="flex items-center gap-2">
                        <SelectedIcon className="w-4 h-4 text-brand-600" />
                        <span className="text-sm font-medium text-gray-900">{selectedOption.label}</span>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <span className="text-xs text-brand-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        {isOpen ? 'Close' : 'Change'}
                    </span>
                    <ChevronDown className={cn(
                        "w-4 h-4 text-gray-400 transition-transform duration-200",
                        isOpen && "rotate-180"
                    )} />
                </div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="pt-4 pb-2">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                                {STYLE_OPTIONS.map((option) => {
                                    const isSelected = value === option.value

                                    return (
                                        <motion.button
                                            key={option.value}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => {
                                                onChange(option.value)
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className={cn(
                                                'relative flex flex-col items-center rounded-xl p-2 transition-all cursor-pointer border h-full',
                                                'outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20',
                                                isSelected
                                                    ? 'bg-brand-50/50 border-brand-200/60 shadow-sm'
                                                    : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200/60',
                                                disabled && 'opacity-50 cursor-not-allowed'
                                            )}
                                        >
                                            <div 
                                                className={cn(
                                                    'w-full aspect-[16/10] sm:aspect-[3/2] rounded-lg mb-2 overflow-hidden transition-all flex items-center justify-center',
                                                    isSelected ? 'bg-brand-100/40' : 'bg-slate-50 opacity-80 group-hover:opacity-100'
                                                )}
                                            >
                                                <div className="w-[80%] h-[80%]">
                                                    {option.graphic}
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-center gap-0.5 w-full px-1">
                                                <span 
                                                    className={cn(
                                                        'text-xs font-medium transition-colors w-full text-center truncate',
                                                        isSelected ? 'text-brand-900' : 'text-gray-600'
                                                    )}
                                                >
                                                    {option.label}
                                                </span>
                                            </div>
                                        </motion.button>
                                    )
                                })}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default StylePicker
